import configs from "../configs";
import { getAuth0Token } from "../ui/utils";
import { preferencesForRemoteSync } from "./preferenceSync";

class UserPreferences {
  constructor() {
    this.storagePrefix = "user_preferences_";
    this.currentUser = null;
    this.saveTimeout = null;
    this.debounceDelay = 1000;
  }

  setAuthUser(user) {
    this.currentUser = user;
  }

  getAuthUser() {
    return this.currentUser;
  }

  getKey(userId) {
    return `${this.storagePrefix}${userId}`;
  }

  /**
   * Loads preferences for a user, prioritizing local storage over the user object.
   * Defaults to the set authUser if no user provided.
   * @param {Object} [user] - The user object (optional if setAuthUser was called)
   * @returns {Object} The user's preferences
   */
  load(user = this.currentUser) {
    // Handle case where user is provided but null/undefined, fallback to current
    const targetUser = user || this.currentUser;

    if (!targetUser || !targetUser.id) return {};

    // 1. Try Local Storage (Most recent)
    try {
      const stored = localStorage.getItem(this.getKey(targetUser.id));
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === "object") {
          return parsed;
        }
      }
    } catch (e) {
      console.warn("Failed to load preferences from local storage:", e);
    }

    // 2. Fallback to User object (Server state)
    if (targetUser.preferences) {
      // Sync server state to local storage for future speed
      this.save(targetUser.id, targetUser.preferences);
      return targetUser.preferences;
    }

    return {};
  }

  /**
   * Saves preferences to local storage and syncs to backend.
   * Can be called as save(preferences) to use current user, or save(userId, preferences).
   */
  save(arg1, arg2) {
    let userId;
    let preferences;

    if (arg2 !== undefined) {
      // save(userId, preferences)
      userId = arg1;
      preferences = arg2;
    } else {
      // save(preferences) using current user
      userId = this.currentUser?.id;
      preferences = arg1;
    }

    if (!userId) {
      console.warn("UserPreferences: No user ID provided for save.");
      return;
    }

    // 1. Local Storage (Immediate)
    try {
      localStorage.setItem(this.getKey(userId), JSON.stringify(preferences));
    } catch (e) {
      console.error("Error saving user preferences:", e);
    }

    // 2. Backend Sync (Debounced)
    // Only sync if it's the current authenticated user (security check)
    // or if we have a token (handled in persist)
    // Check if the user ID matches the current user to prevent saving cross-user data inadvertently
    // (though load() handles authUser vs other user reading fine).
    // If saving for "anonymous", we skip backend.
    if (this.currentUser && this.currentUser.id === userId) {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }

      this.saveTimeout = setTimeout(() => {
        this.persist(userId, preferences);
      }, this.debounceDelay);
    }
  }

  async persist(userId, preferences) {
    try {
      const url = configs["CUSTOM_API_URL"] + "/preferences";
      const auth0token = await getAuth0Token();
      const remotePreferences = preferencesForRemoteSync(preferences);

      await fetch(url, {
        method: "PUT",
        body: JSON.stringify(remotePreferences),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth0token}`,
          Accept: "application/json"
        }
      });
    } catch (error) {
      console.error("Failed to sync preferences to backend:", error);
    }
  }

  // Alias for compatibility
  savePreferences(userId, preferences) {
    this.save(userId, preferences);
  }
}

const userPreferences = new UserPreferences();
export default userPreferences;
